import { useEffect, useRef, useCallback, useState } from "react";
import { useDebounce } from "./useDebounce";

interface UseAutoSaveProps<T> {
  data: T;
  onSave: (data: T) => Promise<void>;
  delay?: number;
  enabled?: boolean;
}

export function useAutoSave<T>({
  data,
  onSave,
  delay = 800,
  enabled = true,
}: UseAutoSaveProps<T>) {
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const isInitialMount = useRef(true);

  const debouncedData = useDebounce(data, delay);

  const save = useCallback(
    async (dataToSave: T) => {
      if (!enabled) return;

      setIsSaving(true);
      setError(null);

      try {
        await onSave(dataToSave);
        setLastSaved(new Date());
      } catch (err) {
        setError(err instanceof Error ? err : new Error("Save failed"));
        console.error("Auto-save error:", err);
      } finally {
        setIsSaving(false);
      }
    },
    [onSave, enabled],
  );

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (debouncedData && enabled) {
      save(debouncedData);
    }
  }, [debouncedData, save, enabled]);

  return {
    isSaving,
    lastSaved,
    error,
    forceSave: () => save(data),
  };
}
